import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PORTAL_NAV } from '../../../constants/portals';
import ProjectsChooserIcon, {
  ProjectsChooserArrow,
  ProjectsChooserCloseIcon,
  ProjectsChooserFolderIcon,
} from './ProjectsChooserIcon';
import styles from './ProjectsChooser.module.css';

const CARD_METADATA = {
  selection: {
    title: (
      <>
        <span className={styles.green}>IN</span>/<span className={styles.red}>OUT</span> Selection
      </>
    ),
    description: 'Review developments and decide what moves into the collection.',
    action: 'Make a selection',
  },
  sellsheets: {
    title: 'Sell Sheets',
    description: 'Turn selected models into clear, ready-to-share product sheets.',
    action: 'Build a sell sheet',
  },
  decisionemails: {
    title: 'Decision Emails',
    description: 'Bring collection decisions together in clear, organised emails.',
    action: 'Prepare an email',
  },
  balance: {
    title: (
      <>
        <span className={styles.amber}>Ordered</span> vs Asked
      </>
    ),
    description: 'See what we ordered next to what Israel asked for — by shape and material, live.',
    action: 'Check the balance',
    badge: 'NEW',
  },
};

function projectCards() {
  const projects = PORTAL_NAV.find((portal) => portal.key === 'projects');
  return (projects?.subsections || [])
    .filter((section) => CARD_METADATA[section.projectsView])
    .map((section) => ({
      view: section.projectsView,
      ...CARD_METADATA[section.projectsView],
    }));
}

export default function ProjectsChooser({ open, onClose, onSelect }) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const pointerStartedOnBackdrop = useRef(false);
  const onCloseRef = useRef(onClose);
  const cards = useMemo(projectCards, []);

  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    if (dialog && !dialog.open) dialog.showModal();
    closeButtonRef.current?.focus({ preventScroll: true });

    return () => {
      if (dialog?.open) dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  const close = () => onCloseRef.current?.();
  const select = (view) => {
    onSelect?.(view);
    close();
  };
  const moveGlow = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty('--mx', `${((event.clientX - rect.left) / rect.width) * 100}%`);
    event.currentTarget.style.setProperty('--my', `${((event.clientY - rect.top) / rect.height) * 100}%`);
  };

  return createPortal(
    <dialog
      ref={dialogRef}
      className={styles.chooserRoot}
      aria-labelledby="projects-chooser-title"
      aria-describedby="projects-chooser-description"
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const buttons = event.currentTarget.querySelectorAll('button:not([disabled])');
        const first = buttons[0], last = buttons[buttons.length - 1];
        const destination = event.shiftKey && document.activeElement === first ? last
          : !event.shiftKey && document.activeElement === last ? first : null;
        if (destination) { event.preventDefault(); destination.focus(); }
      }}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onPointerDown={(event) => {
        pointerStartedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onPointerUp={(event) => {
        if (pointerStartedOnBackdrop.current && event.target === event.currentTarget) close();
        pointerStartedOnBackdrop.current = false;
      }}
      onPointerCancel={() => {
        pointerStartedOnBackdrop.current = false;
      }}
    >
      <div className={styles.dialogInner}>
        <header className={styles.titleRow}>
          <div>
            <h2 id="projects-chooser-title">Projects</h2>
            <p id="projects-chooser-description" className={styles.subtitle}>
              Great work starts here. Choose your workspace.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            className={styles.close}
            type="button"
            aria-label="Close department chooser"
            onClick={close}
          >
            <ProjectsChooserCloseIcon className={styles.closeIcon} />
          </button>
        </header>

        <div className={styles.cards}>
          {cards.map((card) => (
            <button
              key={card.view}
              className={styles.card}
              type="button"
              onClick={() => select(card.view)}
              onPointerMove={moveGlow}
            >
              {card.badge && <span className={styles.badge}>{card.badge}</span>}
              <span className={styles.visual}>
                <span className={styles.iconPlate}>
                  <ProjectsChooserIcon name={card.view} className={styles.projectIcon} />
                </span>
              </span>
              <span className={styles.copy}>
                <span className={styles.cardTitle}>{card.title}</span>
                <span className={styles.description}>{card.description}</span>
                <span className={styles.go}>
                  {card.action}
                  <span className={styles.arrow}>
                    <ProjectsChooserArrow className={styles.arrowIcon} />
                  </span>
                </span>
              </span>
            </button>
          ))}
        </div>

        <footer className={styles.footer}>
          <span className={styles.footerNote}>
            <ProjectsChooserFolderIcon className={styles.folderIcon} />
            Your next move, all in one place.
          </span>
          <span className={styles.keys} data-hide-on-touch>
            <kbd>Tab</kbd> to move <kbd>Enter</kbd> to open <kbd>Esc</kbd> to close
          </span>
        </footer>
      </div>
    </dialog>,
    document.body
  );
}
